import React, { useState } from 'react';
import { updatePassword, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useAuth } from './AuthContext';
import { del } from './apiClient';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Card, CardHeader, CardContent,
  TextField, Box, Alert, CircularProgress, Button, Typography, Divider,
  InputAdornment, IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function ProfilePage() {
  const { currentUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPasswordUser = currentUser.providerData.some(
    (provider) => provider.providerId === 'password'
  );

  const getButtonProps = () => {
    if (isUpdatingPassword) {
      return { disabled: true, text: <CircularProgress size={24} /> };
    }
    if (newPassword.length > 0 && newPassword.length < 6) {
      return { disabled: true, text: 'Password too short' };
    }
    if (newPassword.length >= 6 && newPassword !== confirmPassword) {
      return { disabled: true, text: "Passwords don't match" };
    }
    if (newPassword === '' || confirmPassword === '') {
      return { disabled: true, text: 'Update Password' };
    }
    return { disabled: false, text: 'Update Password' };
  };

  const buttonProps = getButtonProps();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsUpdatingPassword(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setIsUpdatingPassword(false);
      return;
    }

    try {
      await updatePassword(currentUser, newPassword);
      setMessage('Password updated successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(`Failed to update password: ${err.message}`);
      console.error(err);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you absolutely sure you want to delete your account? This will permanently delete all of your data, including API keys and jobs. This action cannot be undone.')) {
      return;
    }
    
    setIsDeleting(true);
    setError('');
    setMessage('');

    try {
      await del('/user');
      await signOut(auth);
    } catch (err) {
      try {
        const response = JSON.parse(err.message);
        setError(response.error || 'Failed to delete account.');
      } catch {
        setError('Failed to delete account. An unknown error occurred.');
      }
      console.error(err);
      setIsDeleting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardHeader title="Your Profile" />
        <CardContent>
          {isPasswordUser && (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6">Change Password</Typography>
              {message && <Alert severity="success">{message}</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              
              <TextField
                type={showNewPassword ? 'text' : 'password'}
                name="newPassword"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={buttonProps.disabled}
              >
                {buttonProps.text}
              </Button>
            </Box>
          )}
          
          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6">Billing</Typography>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/billing"
              sx={{ mt: 1 }}
            >
              Manage Billing
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6" color="error">Delete Account</Typography>
            <Typography variant="body2" sx={{ my: 1 }}>
              This will permanently delete your account and all associated data. This action cannot be undone.
            </Typography>
            <Button variant="outlined" color="error" onClick={handleDeleteAccount} disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={24} /> : 'Delete My Account'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
